import styled from "styled-components";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import { useContext, useEffect, useState } from "react";
import {
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from "@mui/material";
import { theme } from "../../../../Themes/Themes";
import {
  INITIAL_MODIFIED_SIMULATION_STATE,
  SimulationsContext,
} from "./SimulationsTab";
import useStore from "../../../../Store/Store";
import { logger } from "../../../../utils/Logger";
import { toggleCellsVisibility } from "./SimulationUtils";
import {
  discardRecordedActions,
  startRecordingActions,
} from "../../../../Store/UndoManager";
import { areObjectsEqual } from "../../../../utils/DeepObjectCompare";
import SimulationControlButtons from "./SimulationControlButtons";

export const SimulationsControls: React.FC = (): JSX.Element => {
  const {
    isRunning,
    setIsRunning,
    currentStep,
    setCurrentStep,
    setIsMaxSD,
    isNewSimulation,
    setIsNewSimulation,
    saveModifiedSimulation,
    currentUnsavedSimulationObject,
    setWiki,
    simulationDescription,
    setSimulationDescription,
    handleCreateSimulation,
  } = useContext(SimulationsContext);

  const {
    simulations,
    repoData,
    currentPath,
    setSimulations,
    isEditingSimulations,
    setIsEditingSimulations,
    selectedSimulationKey,
    setSelectedSimulationKey,
    setCurrentUnsavedSimulationObject,
    cellsWithUnsavedVisibilityFlag,
    transitionToNextSimulation,
    isSimulationFullSizeMode,
    floatingStepDescriptionVisible,
    setFloatingStepDescriptionVisible,
  } = useStore((state) => ({
    simulations: state.simulationsState.simulations,
    repoData: state.repoData,
    currentPath: state.currentPath,
    setSimulations: state.setSimulations,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    setIsEditingSimulations: state.setIsEditingSimulations,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    setSelectedSimulationKey: state.setSelectedSimulationKey,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
    cellsWithUnsavedVisibilityFlag:
      state.simulationsState.cellsWithUnsavedVisibilityFlag,
    transitionToNextSimulation: state.transitionToNextSimulation,
    isSimulationFullSizeMode: state.isSimulationFullSizeMode,
    floatingStepDescriptionVisible: state.floatingStepDescriptionVisible,
    setFloatingStepDescriptionVisible: state.setFloatingStepDescriptionVisible,
  }));
  const [isModified, setIsModified] = useState(false);

  const handleCancel = async () => {
    if (isEditingSimulations === "Description") {
      setSimulationDescription(
        currentUnsavedSimulationObject.description || ""
      );
    }
    await setIsEditingSimulations("");
    if (isNewSimulation) {
      // This creates a new object that includes all attributes from the read-only object except the new simulation
      const filteredObject = Object.fromEntries(
        Object.entries(simulations).filter(
          ([key]) => key !== selectedSimulationKey
        )
      );
      await setSelectedSimulationKey("");
      setWiki(repoData[currentPath]?.wiki || "");
      await setSimulations(filteredObject);
      setIsNewSimulation(false);
    }

    await setCurrentUnsavedSimulationObject(
      simulations[selectedSimulationKey] || INITIAL_MODIFIED_SIMULATION_STATE
    );
    if (!selectedSimulationKey) {
      await discardRecordedActions("CURRENT_UNSAVED_SIMULATION_OBJECT");
    }
    useStore.getState().setCellsWithUnsavedVisibilityFlag({});
    setSimulationDescription("");
    await discardRecordedActions("SIMULATION_MODIFIED");
  };

  const handleSave = async () => {
    if (!isModified) return;
    if (isEditingSimulations === "Description") {
      await setCurrentUnsavedSimulationObject({
        ...currentUnsavedSimulationObject,
        description: simulationDescription,
      });
      await setSimulations({
        ...simulations,
        [currentUnsavedSimulationObject.name]: {
          ...currentUnsavedSimulationObject,
          description: simulationDescription,
        },
      });
    }
    await setIsEditingSimulations("");
    if (isNewSimulation) {
      setIsNewSimulation(false);
    }
    // loop through the set of cellsWithUnsavedVisibilityFlag and set the visible flag in each of the cells in repoData
    Object.entries(cellsWithUnsavedVisibilityFlag).forEach(
      ([cellId, visible]) => {
        if (cellId in repoData) {
          useStore
            .getState()
            .setRepoObjectAttribute(cellId, "visible", visible);
        }
      }
    );

    await saveModifiedSimulation();
    useStore.getState().setCellsWithUnsavedVisibilityFlag({});
    setSimulationDescription("");
  };

  // Maximize SD when is editing mode or running mode (unless user manually toggled)
  useEffect(() => {
    const userManuallyToggledSDMaximize =
      useStore.getState().userManuallyToggledSDMaximize;
    if ((isEditingSimulations || isRunning) && !userManuallyToggledSDMaximize) {
      setIsMaxSD(true);
    }
  }, [setIsMaxSD, isEditingSimulations, isRunning]);

  // Posts to drawio to select cell based on current step
  useEffect(() => {
    const setFlag = useStore.getState().setFlag;
    const cellId = currentUnsavedSimulationObject.simSteps.find(
      (step) => step.simStepId === currentStep
    )?.diagramNodeId;
    let flags = useStore.getState().flags;
    if (
      flags.isChangingPath ||
      flags.isHandlingSelectionChange ||
      flags.isAddingNewSimStep
    ) {
      return;
    }
    setFlag("isChangingPath", true);
    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "CURRENT_PATH_CHANGE",
        data: {
          selectId: cellId,
          clearSelection: !isRunning && !isEditingSimulations,
          isLinked: false,
          isDrawioEventOrigin: false,
        },
      })
      .finally(() => {
        setFlag("isChangingPath", false);
        setFlag("isHandlingSelectionChange", false);
      });
  }, [
    currentStep,
    currentUnsavedSimulationObject.simSteps,
    isRunning,
    isEditingSimulations,
  ]);

  // toggles all cells visiblity when in edit mode
  useEffect(() => {
    if (isEditingSimulations === "SimSteps") {
      toggleCellsVisibility(true).catch((err) => logger.error(err));
    } else {
      // get all cells that are not in the current simulation
      const ignoreList = useStore
        .getState()
        .currentUnsavedSimulationObject.simSteps.map(
          (step) => step.diagramNodeId
        );
      toggleCellsVisibility(false, ignoreList).catch((err) =>
        logger.error(err)
      );
    }
  }, [isEditingSimulations]);

  // disable simulation save button when currentUnsavedSimulationObject is equal to the saved simulation object or when
  // there are no changes made to the simulation
  useEffect(() => {
    if (
      !selectedSimulationKey ||
      !currentUnsavedSimulationObject ||
      !simulations[selectedSimulationKey]
    ) {
      return;
    }

    const isModified =
      Object.keys(cellsWithUnsavedVisibilityFlag).length !== 0 ||
      isNewSimulation ||
      !areObjectsEqual(
        currentUnsavedSimulationObject,
        simulations[selectedSimulationKey]
      );

    setIsModified(isModified);
  }, [
    currentUnsavedSimulationObject,
    selectedSimulationKey,
    simulations,
    isNewSimulation,
    cellsWithUnsavedVisibilityFlag,
    setIsModified,
  ]);

  // Don't render controls section when in full-size mode
  if (isSimulationFullSizeMode) {
    return null;
  }

  return (
    <div
      className="controls"
      style={isEditingSimulations ? { marginBottom: "20px" } : {}}
    >
      <SimulationControlButtons />
      {isEditingSimulations && (
        <ToggleButtonGroup
          color="primary"
          value={isEditingSimulations}
          exclusive
          style={{ height: "30px", borderRadius: "30px" }}
          // change border radio to 30px
          sx={{
            "& .MuiToggleButton-root": {
              borderRadius: "30px",
              border: `1px solid ${theme.custom.lighterPink}`,
              color: theme.custom.lighterPink,
            },
          }}
          onChange={async (event, newValue) => {
            return await setIsEditingSimulations(newValue);
          }}
          aria-label="Platform"
        >
          <ToggleButton value="SimSteps" style={{ marginRight: "5px" }}>
            SimSteps
          </ToggleButton>
          <ToggleButton value="Description">Description</ToggleButton>
        </ToggleButtonGroup>
      )}

      <div className="rightButtonsWrapper">
        {isEditingSimulations ? (
          <>
            <div
              className="EditWikiButtonWrapper"
              onClick={handleCancel}
              style={{ width: "50px" }}
            >
              <Typography fontSize="12px" color="primary">
                Cancel{" "}
              </Typography>
            </div>
            <div
              id="simulationSaveButton"
              className={
                "EditWikiButtonWrapper" + (!isModified ? " disabled" : "")
              }
              onClick={handleSave}
              style={{ marginLeft: "10px", width: "50px" }}
            >
              <Typography fontSize="12px">Save</Typography>
            </div>
          </>
        ) : (
          <>
            <Tooltip title="Create a new simulation">
              <div
                onClick={handleCreateSimulation}
                style={{
                  width: "30px",
                  marginRight: "10px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `solid 1px ${theme.custom.lighterPink}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#ff9088";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <AddRoundedIcon
                  style={{
                    width: "18px",
                    height: "18px",
                    color: theme.custom.lighterPink,
                  }}
                />
              </div>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};

export default SimulationsControls;
